import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  GraduationCap,
  Globe,
  Wallet,
  Sparkles,
  Trophy,
  Compass,
  Loader2,
  User,
  Mail,
  ShieldCheck,
  Building2,
  ChevronRight,
} from 'lucide-react';
import universityData from './data/universityData';
import {
  useVisaRecommenderPreference,
  recommendationToEvaluationFields,
} from '@/hooks/useVisaRecommenderPreference';
import PrefillBanner from '@/components/common/PrefillBanner';
import PrivacyConsentNotice from '@/components/common/PrivacyConsentNotice';
import { usePageMeta } from '@/hooks/useSEO';
import { trackEligibilitySubmit } from '@/services/analytics';
import { LeadPhoneField } from '@/components/lead-quality/LeadPhoneField';
import { useLeadQualityEngine } from '@/components/lead-quality/useLeadQualityEngine';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SOURCE_TAG = 'study-abroad-free-profile-evaluation';

// ───────────────────────────────────────────────────────────────────────
// Option pools
// ───────────────────────────────────────────────────────────────────────
const qualificationOptions = [
  { value: '12th', label: 'Class 12th / HSC' },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
];

const englishTestOptions = [
  { value: 'none', label: 'Not Taken Yet' },
  { value: 'ielts', label: 'IELTS' },
  { value: 'pte', label: 'PTE Academic' },
  { value: 'toefl', label: 'TOEFL iBT' },
];

const englishScoreBands = {
  ielts: [
    { value: 'lt55', label: 'Below 5.5', strength: 1 },
    { value: '55-65', label: '5.5 – 6.5', strength: 2 },
    { value: '65-75', label: '6.5 – 7.5', strength: 3 },
    { value: 'gt75', label: '7.5 and above', strength: 4 },
  ],
  pte: [
    { value: 'lt50', label: 'Below 50', strength: 1 },
    { value: '50-65', label: '50 – 65', strength: 2 },
    { value: '65-79', label: '65 – 79', strength: 3 },
    { value: 'gt79', label: '79 and above', strength: 4 },
  ],
  toefl: [
    { value: 'lt72', label: 'Below 72', strength: 1 },
    { value: '72-90', label: '72 – 90', strength: 2 },
    { value: '90-105', label: '90 – 105', strength: 3 },
    { value: 'gt105', label: '105 and above', strength: 4 },
  ],
};

const destinationOptions = [
  { value: 'germany', label: 'Germany', flag: '🇩🇪' },
  { value: 'uk', label: 'United Kingdom', flag: '🇬🇧' },
  { value: 'canada', label: 'Canada', flag: '🇨🇦' },
  { value: 'australia', label: 'Australia', flag: '🇦🇺' },
  { value: 'ireland', label: 'Ireland', flag: '🇮🇪' },
  { value: 'new-zealand', label: 'New Zealand', flag: '🇳🇿' },
  { value: 'uae', label: 'Dubai / UAE', flag: '🇦🇪' },
];

const budgetOptions = [
  { value: 'lt5', label: 'Under ₹5 Lakhs', tier: 1 },
  { value: '5-10', label: '₹5 – 10 Lakhs', tier: 2 },
  { value: '10-20', label: '₹10 – 20 Lakhs', tier: 3 },
  { value: 'gt20', label: '₹20 Lakhs+', tier: 4 },
];

// Map destination value → countrySlug used in universityData
const destinationCountryMap = {
  germany: ['germany'],
  uk: ['uk', 'united-kingdom'],
  canada: ['canada'],
  australia: ['australia'],
  ireland: ['ireland'],
  'new-zealand': ['new-zealand'],
  uae: ['uae', 'dubai', 'united-arab-emirates'],
};

const COUNTRY_DISPLAY_NAMES = {
  germany: 'Germany',
  uk: 'United Kingdom',
  canada: 'Canada',
  australia: 'Australia',
  ireland: 'Ireland',
  'new-zealand': 'New Zealand',
  uae: 'Dubai / UAE',
};

// ───────────────────────────────────────────────────────────────────────
// Evaluation engine (frontend, lightweight)
// ───────────────────────────────────────────────────────────────────────
function evaluateProfile(form) {
  let score = 0;

  // Academic strength
  const pct = parseFloat(form.percentage);
  if (!Number.isNaN(pct)) {
    // Treat CGPA-like values (<= 10) by multiplying by 9.5
    const normalized = pct <= 10 ? pct * 9.5 : pct;
    if (normalized >= 85) score += 3;
    else if (normalized >= 70) score += 2;
    else if (normalized >= 55) score += 1;
  }

  // Qualification
  if (form.qualification === 'masters') score += 2;
  else if (form.qualification === 'bachelors') score += 1.5;
  else if (form.qualification === '12th') score += 1;

  // English proficiency
  if (form.englishTest && form.englishTest !== 'none' && form.englishScore) {
    const band = englishScoreBands[form.englishTest]?.find((b) => b.value === form.englishScore);
    if (band) score += band.strength;
  }

  // Budget tier
  const budget = budgetOptions.find((b) => b.value === form.budget);
  if (budget) score += budget.tier - 1; // 0..3

  // Tier classification
  let tier;
  if (score >= 8) {
    tier = {
      key: 'excellent',
      label: 'Excellent Fit',
      blurb:
        'Your academic strength, English proficiency, and budget align with premier global universities. You\u2019re in the strongest application bracket.',
      Icon: Trophy,
      accent: 'from-[#C5A028] to-[#EAB308]',
    };
  } else if (score >= 5) {
    tier = {
      key: 'good',
      label: 'Good Progress',
      blurb:
        'You qualify for several strong programmes. A few targeted improvements (test scores, profile shortlisting) will unlock top-tier universities.',
      Icon: Sparkles,
      accent: 'from-emerald-400 to-emerald-600',
    };
  } else {
    tier = {
      key: 'counselling',
      label: 'Counselling Required',
      blurb:
        'A 1:1 counselling session will help us craft a personalised roadmap, suggest scholarships, and identify the best-fit universities for your profile.',
      Icon: Compass,
      accent: 'from-blue-400 to-blue-600',
    };
  }

  // Recommended universities from universityData
  const slugs = destinationCountryMap[form.destination] || [];
  const matches = Object.entries(universityData)
    .filter(([, uni]) =>
      slugs.includes((uni.countrySlug || '').toLowerCase()),
    )
    .map(([slug, uni]) => ({ slug, ...uni }));

  // Budget bias: lower budget → prefer Public; higher budget → both
  const budgetTier = budget?.tier || 2;
  const recommended = matches
    .filter((uni) => {
      if (budgetTier <= 2) return (uni.type || '').toLowerCase().includes('public') || (uni.type || '').toLowerCase().includes('government');
      return true;
    })
    .slice(0, 6);

  // If filter wiped everything, fall back to first 6 matches
  const finalRecs = recommended.length > 0 ? recommended : matches.slice(0, 6);

  return { score, tier, recommended: finalRecs };
}

// ───────────────────────────────────────────────────────────────────────
// Reusable UI bits
// ───────────────────────────────────────────────────────────────────────
const FieldLabel = ({ children }) => (
  <label className="block text-[11px] font-bold tracking-[0.18em] text-yellow-700 uppercase mb-2">
    {children}
  </label>
);

const SelectChip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left px-4 py-3 rounded-xl border transition-all ${
      active
        ? 'bg-[#C5A028]/15 border-[#C5A028] text-gray-900 shadow-lg shadow-[#C5A028]/10'
        : 'bg-white border-slate-200 text-gray-800 hover:bg-[#C5A028]/5 hover:border-[#C5A028]/40 hover:shadow-sm'
    }`}
  >
    <span className="text-sm font-semibold">{children}</span>
  </button>
);

const StepHeader = ({ step, total, icon: Icon, title, subtitle }) => (
  <div className="mb-7">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-[#C5A028]/15 border border-[#C5A028]/30 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#C5A028]" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-gray-600">
          Step {step} of {total}
        </div>
        <h2 className="text-2xl sm:text-3xl text-gray-900 sa-display font-semibold leading-tight">
          {title}
        </h2>
      </div>
    </div>
    {subtitle && <p className="text-sm text-gray-700 max-w-xl">{subtitle}</p>}
  </div>
);

// ───────────────────────────────────────────────────────────────────────
// Main Component
// ───────────────────────────────────────────────────────────────────────
const TOTAL_STEPS = 3;

const FreeProfileEvaluationPage = () => {
  usePageMeta(
    'Free Study Abroad Profile Evaluation | Get Your Eligibility Score',
    'Free 5-minute profile evaluation by MEA-approved counsellors. Get your eligibility score, country fit, scholarship potential & visa-success prediction for Germany, UK, Canada, Australia & Ireland.',
  );
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    qualification: '',
    percentage: '',
    englishTest: '',
    englishScore: '',
    destination: '',
    budget: '',
    name: '',
    email: '',
    whatsappOptIn: true,
  });
  const lqe = useLeadQualityEngine();
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [result, setResult] = useState(null);

  // "Best Country for Me" recommender persistence — auto-fills destination + budget tier.
  const { pref: visaPref, clear: clearVisaPref } = useVisaRecommenderPreference();
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    const next = recommendationToEvaluationFields(visaPref);
    if (!next) {
      setPrefilled(false);
      return;
    }
    setForm(prev => {
      const updates = {};
      Object.keys(next).forEach((k) => {
        if (!prev[k]) updates[k] = next[k];
      });
      if (Object.keys(updates).length === 0) return prev;
      setPrefilled(true);
      return { ...prev, ...updates };
    });
  }, [visaPref]);

  const dismissPrefill = () => {
    setPrefilled(false);
    clearVisaPref();
  };

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const {
    qualification,
    percentage,
    englishTest,
    englishScore,
    destination,
    budget,
    name,
    email,
  } = form;

  const englishScoreList = useMemo(
    () => (englishTest && englishTest !== 'none' ? englishScoreBands[englishTest] : []),
    [englishTest],
  );

  // Validators per step
  const isStepValid = useMemo(() => {
    if (step === 1) {
      if (!qualification) return false;
      if (!percentage) return false;
      if (!englishTest) return false;
      if (englishTest !== 'none' && !englishScore) return false;
      return true;
    }
    if (step === 2) return Boolean(destination && budget);
    if (step === 3) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      const phoneOk = lqe.nationalDigits.length > 0;
      return Boolean(name.trim() && emailOk && phoneOk);
    }
    return false;
  }, [step, qualification, percentage, englishTest, englishScore, destination, budget, name, email, lqe.nationalDigits]);

  const goNext = () => setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    if (!isStepValid) return;
    const guard = await lqe.guardSubmit();
    if (!guard.ok) return;
    setSubmitting(true);
    setSubmissionError('');

    const evalResult = evaluateProfile(form);

    // Build message for the CRM with structured payload
    const lines = [
      `[Source — ${SOURCE_TAG}]`,
      `Eligibility Tier: ${evalResult.tier.label}`,
      `Internal Score: ${evalResult.score.toFixed(1)}`,
      '',
      'Academic Profile:',
      `  Qualification: ${qualificationOptions.find((o) => o.value === form.qualification)?.label || form.qualification}`,
      `  Aggregate %/CGPA: ${form.percentage}`,
      `  English Test: ${englishTestOptions.find((o) => o.value === form.englishTest)?.label || form.englishTest}`,
      form.englishTest && form.englishTest !== 'none'
        ? `  English Score Band: ${englishScoreList.find((b) => b.value === form.englishScore)?.label || form.englishScore}`
        : null,
      '',
      'Destination & Budget:',
      `  Country: ${COUNTRY_DISPLAY_NAMES[form.destination] || form.destination}`,
      `  Annual Tuition Budget: ${budgetOptions.find((b) => b.value === form.budget)?.label || form.budget}`,
      '',
      `WhatsApp Opt-in: ${form.whatsappOptIn ? 'Yes' : 'No'}`,
      `Top recommended slugs: ${evalResult.recommended.map((u) => u.slug).join(', ') || 'none'}`,
    ].filter(Boolean);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: lqe.phone,
      ...guard.payloadMeta,
      service_type: 'Study Abroad - Free Profile Evaluation',
      country: COUNTRY_DISPLAY_NAMES[form.destination] || form.destination,
      qualification: form.qualification,
      experience: '',
      message: lines.join('\n'),
      source: SOURCE_TAG,
    };

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // We surface the result even if CRM had a soft hiccup — lead is captured in DB
      if (lqe.interpretResponse(res)) return;
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Profile evaluation lead submit non-200:', res.status, text);
        }
      }
      lqe.markSubmitSuccess();
      setResult(evalResult);
      // Conversion: high-value eligibility lead — fire AFTER the
      // network call succeeds. evalResult.tier.value lets Smart Bidding
      // optimise toward higher-tier (more qualified) leads.
      trackEligibilitySubmit(SOURCE_TAG, {
        country: payload.country,
        qualification: payload.qualification,
        tier: evalResult?.tier?.label || 'unknown',
      });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Profile evaluation submission failed:', err);
      }
      // Still show the evaluation — lead can be retried; never block UX
      setResult(evalResult);
      setSubmissionError(
        'We could not reach the server in real time, but your profile has been evaluated. Our team will be in touch via WhatsApp shortly.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ───────────────────────────────────────────────
  // Result Scorecard
  // ───────────────────────────────────────────────
  if (result) {
    const TierIcon = result.tier.Icon;
    return (
      <div className="min-h-screen bg-[#020617] text-white py-16 sm:py-24" data-testid="profile-evaluation-result">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-b from-white/[0.04] to-transparent border border-white/10 rounded-3xl p-6 sm:p-10 shadow-2xl shadow-black/40">
            {/* Tier badge */}
            <div className="flex flex-col items-center text-center mb-8">
              <div
                className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${result.tier.accent} flex items-center justify-center shadow-xl mb-4`}
              >
                <TierIcon className="w-10 h-10 text-[#020617]" />
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#C5A028] mb-2">
                Your Eligibility Tier
              </div>
              <h1 className="sa-display text-3xl sm:text-5xl font-semibold mb-3" data-testid="result-tier-label">
                {result.tier.label}
              </h1>
              <p className="text-slate-300 max-w-xl text-sm sm:text-base">
                {result.tier.blurb}
              </p>
              {submissionError && (
                <p className="mt-3 text-xs text-amber-400/90 max-w-md">{submissionError}</p>
              )}
            </div>

            {/* Recommended Universities */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-[#C5A028]" />
                <h2 className="text-lg font-semibold text-white sa-display">
                  Recommended Universities — {COUNTRY_DISPLAY_NAMES[form.destination] || form.destination}
                </h2>
              </div>

              {result.recommended.length === 0 ? (
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 text-sm text-slate-400">
                  Our specialised counsellor will hand-pick universities that match your profile and budget. Book a
                  free 1:1 session to receive a personalised shortlist.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="result-recommendations">
                  {result.recommended.map((uni) => (
                    <Link
                      key={uni.slug}
                      to={`/study-abroad/${uni.slug}/`}
                      className="group bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-[#C5A028]/40 rounded-xl p-4 transition-all"
                      data-testid={`result-uni-${uni.slug}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-white truncate">
                            {uni.name}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {uni.city} · {uni.type}
                          </div>
                          {uni.averageTuition && (
                            <div className="text-[11px] text-[#C5A028] mt-1.5 font-medium">
                              {uni.averageTuition.amount} {uni.averageTuition.currency}/{uni.averageTuition.period}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-[#C5A028] mt-1 shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Primary CTA */}
            <Link
              to={`/study-abroad/book-counselling/?source=${SOURCE_TAG}-followup&country=${form.destination}&qualification=${form.qualification}&eligibility=${result.tier.key}`}
              className="block w-full text-center bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold py-4 px-6 rounded-2xl text-sm sm:text-base transition-all hover:shadow-lg hover:shadow-[#C5A028]/30 hover:-translate-y-0.5"
              data-testid="result-primary-cta"
            >
              Connect with a Dedicated Specialist & Lock Shortlist
              <ArrowRight className="inline-block w-4 h-4 ml-2 -mt-0.5" />
            </Link>

            <div className="text-center mt-4 text-xs text-slate-500">
              Your details have been securely captured. A ZAFCO Study Abroad counsellor will reach out via WhatsApp
              within 24 working hours.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────
  // Wizard UI
  // ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#020617] text-white py-12 sm:py-20" data-testid="profile-evaluation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Eyebrow + Heading */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#C5A028]/10 border border-[#C5A028]/30 text-[10.5px] font-bold tracking-[0.22em] text-[#C5A028] uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            Free Profile Evaluation
          </span>
          <h1 className="mt-4 sa-display text-3xl sm:text-5xl font-semibold text-white leading-tight">
            Discover Your <em className="text-[#C5A028] not-italic">Global Fit</em> in 60 seconds.
          </h1>
          <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-xl mx-auto">
            Answer a few questions about your academics, destination, and budget — our engine will instantly map
            your strongest university shortlist and eligibility tier.
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-8" data-testid="wizard-progress">
          <div className="flex items-center justify-between text-[10px] tracking-[0.18em] text-slate-400 uppercase mb-2 font-semibold">
            <span>Step {step} / {TOTAL_STEPS}</span>
            <span>{Math.round((step / TOTAL_STEPS) * 100)}% complete</span>
          </div>
          <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#C5A028] to-[#EAB308] transition-all duration-500"
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        {/* Step card */}
        <div className="bg-gradient-to-b from-white/[0.04] to-transparent border border-white/10 rounded-3xl p-6 sm:p-9 shadow-xl shadow-black/30">
          {prefilled && (
            <div className="mb-5">
              <PrefillBanner
                variant="dark"
                onDismiss={dismissPrefill}
                message={<>We&apos;ve set <em>Destination</em> and <em>Budget</em> based on your quiz answers — feel free to change.</>}
                testid="evaluation-prefill-banner"
              />
            </div>
          )}
          {step === 1 && (
            <>
              <StepHeader
                step={1}
                total={TOTAL_STEPS}
                icon={GraduationCap}
                title="Academic Profile"
                subtitle="Tell us about your current academic standing and language proficiency."
              />

              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {qualificationOptions.map((opt) => (
                  <SelectChip
                    key={opt.value}
                    active={form.qualification === opt.value}
                    onClick={() => updateField('qualification', opt.value)}
                    testId={`qual-${opt.value}`}
                  >
                    {opt.label}
                  </SelectChip>
                ))}
              </div>

              <div className="mb-6">
                <FieldLabel>Aggregate Percentage / CGPA</FieldLabel>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.percentage}
                  onChange={(e) => updateField('percentage', e.target.value)}
                  placeholder="e.g. 78  or  8.4"
                  className="w-full bg-white border border-slate-200 focus:border-[#C5A028] focus:ring-2 focus:ring-[#C5A028]/20 rounded-xl px-4 py-3 text-gray-900 text-sm outline-none transition-all placeholder:text-gray-500"
                  data-testid="input-percentage"
                />
                <p className="mt-1.5 text-[11px] text-slate-500">
                  Use percentage (0-100) or CGPA (out of 10). Both are accepted.
                </p>
              </div>

              <div className="mb-6">
                <FieldLabel>English Proficiency</FieldLabel>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {englishTestOptions.map((opt) => (
                    <SelectChip
                      key={opt.value}
                      active={form.englishTest === opt.value}
                      onClick={() => {
                        updateField('englishTest', opt.value);
                        if (opt.value === 'none') updateField('englishScore', '');
                      }}
                      testId={`eng-${opt.value}`}
                    >
                      {opt.label}
                    </SelectChip>
                  ))}
                </div>
              </div>

              {form.englishTest && form.englishTest !== 'none' && (
                <div data-testid="english-subscore-block">
                  <FieldLabel>
                    {form.englishTest.toUpperCase()} Score Band
                  </FieldLabel>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {englishScoreList.map((band) => (
                      <SelectChip
                        key={band.value}
                        active={form.englishScore === band.value}
                        onClick={() => updateField('englishScore', band.value)}
                        testId={`engscore-${band.value}`}
                      >
                        {band.label}
                      </SelectChip>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <StepHeader
                step={2}
                total={TOTAL_STEPS}
                icon={Globe}
                title="Destination & Budget"
                subtitle="Where do you want to study and what is your annual tuition capacity?"
              />

              <div className="mb-6">
                <FieldLabel>Intended Study Destination</FieldLabel>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {destinationOptions.map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => updateField('destination', opt.value)}
                      data-testid={`dest-${opt.value}`}
                      className={`flex items-center gap-2 px-3 py-3 rounded-xl border transition-all text-left ${
                        form.destination === opt.value
                          ? 'bg-[#C5A028]/15 border-[#C5A028] text-gray-900'
                          : 'bg-white border-slate-200 text-gray-800 hover:bg-[#C5A028]/5 hover:border-[#C5A028]/40'
                      }`}
                    >
                      <span className="text-lg">{opt.flag}</span>
                      <span className="text-sm font-semibold">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <FieldLabel>Annual Tuition Budget Capacity</FieldLabel>
                <div className="grid sm:grid-cols-2 gap-2">
                  {budgetOptions.map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => updateField('budget', opt.value)}
                      data-testid={`budget-${opt.value}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                        form.budget === opt.value
                          ? 'bg-[#C5A028]/15 border-[#C5A028] text-gray-900'
                          : 'bg-white border-slate-200 text-gray-800 hover:bg-[#C5A028]/5 hover:border-[#C5A028]/40'
                      }`}
                    >
                      <Wallet className="w-4 h-4 text-[#C5A028]" />
                      <span className="text-sm font-semibold">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <StepHeader
                step={3}
                total={TOTAL_STEPS}
                icon={ShieldCheck}
                title="Lock Your Shortlist"
                subtitle="Share your contact details — we'll deliver your personalised evaluation report instantly."
              />

              <div className="space-y-5">
                <div>
                  <FieldLabel>Full Name</FieldLabel>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="As on your passport"
                      className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 focus:border-[#C5A028] focus:ring-2 focus:ring-[#C5A028]/20 rounded-xl text-gray-900 text-sm outline-none transition-all placeholder:text-gray-500"
                      data-testid="input-name"
                    />
                  </div>
                </div>

                <div>
                  <FieldLabel>Primary Email Address</FieldLabel>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 focus:border-[#C5A028] focus:ring-2 focus:ring-[#C5A028]/20 rounded-xl text-gray-900 text-sm outline-none transition-all placeholder:text-gray-500"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <FieldLabel>WhatsApp Number *</FieldLabel>
                  <LeadPhoneField engine={lqe} variant="gold" showLabel={false} testId="input-phone" />
                </div>

                <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-700 select-none">
                  <input
                    type="checkbox"
                    checked={form.whatsappOptIn}
                    onChange={(e) => updateField('whatsappOptIn', e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 accent-[#C5A028]"
                    data-testid="whatsapp-optin"
                  />
                  <span>
                    Yes, please send me eligibility updates, scholarship alerts, and university intake reminders
                    via WhatsApp.
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Navigation footer */}
          {step === TOTAL_STEPS && (
            <div className="mt-8 pt-6 border-t border-white/10">
              <PrivacyConsentNotice tone="dark" testid="sa-evaluation-privacy-consent" />
            </div>
          )}
          <div className={`${step === TOTAL_STEPS ? 'mt-4' : 'mt-8 pt-6 border-t border-white/10'} flex items-center justify-between`}>
            <button
              type="button"
              onClick={goBack}
              disabled={step === 1}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                step === 1
                  ? 'text-slate-600 cursor-not-allowed'
                  : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
              data-testid="wizard-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {step < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!isStepValid}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${
                  isStepValid
                    ? 'bg-[#C5A028] hover:bg-[#EAB308] text-black hover:shadow-lg hover:shadow-[#C5A028]/30 hover:-translate-y-0.5'
                    : 'bg-white/[0.06] text-slate-500 cursor-not-allowed'
                }`}
                data-testid="wizard-next"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isStepValid || submitting}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${
                  isStepValid && !submitting
                    ? 'bg-[#C5A028] hover:bg-[#EAB308] text-black hover:shadow-lg hover:shadow-[#C5A028]/30 hover:-translate-y-0.5'
                    : 'bg-white/[0.06] text-slate-500 cursor-not-allowed'
                }`}
                data-testid="wizard-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Evaluating…
                  </>
                ) : (
                  <>
                    Get My Evaluation
                    <CheckCircle className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-6 text-center text-[11px] text-slate-500 tracking-wide">
          Powered by ZAFCO Study Abroad · No spam · Reviewed by certified counsellors
        </div>
      </div>
    </div>
  );
};

export default FreeProfileEvaluationPage;
